import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { CmsImageSlideshow as CmsImage } from "../lib/images";

/**
 * ServiceCard — tarjeta cromada con foto + título abajo, animación hover que
 * revela la descripción con un chip cian "Ver detalle". Compartida por el
 * carrusel de la home (`ServiceCarousel`) y la cuadrícula fija de `/servicios`.
 *
 * Requiere que en la página consumidora se inyecten los estilos globales
 * `.metal-card` / `.face` — ambos definidos en `service-card.css`.
 */
export default function ServiceCard({ service, index }) {
  const { pick } = useI18n();
  const s = service;
  return (
    <Link
      to={`/servicios/${s.slug}`}
      aria-label={pick(s.title_es, s.title_en, s.title_pt)}
      data-testid={`service-card-${index}`}
      className="metal-card relative block w-full rounded-[22px]"
    >
      <div className="face aspect-[5/4] group/face">
        {/* Service photo */}
        <CmsImage
          slotKey={s.slot}
          defaultSrc={s.img}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-500 group-hover/face:scale-110"
        />
        {/* Soft vignette so the title stays readable but the photo shows */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.28)_75%,rgba(0,0,0,0.55)_100%)]" />
        {/* Cyan tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/8 via-transparent to-emerald-500/8 mix-blend-screen" />

        {/* Title overlay — hides on hover so description takes centre-stage */}
        <div className="absolute inset-x-0 bottom-0 pb-3 sm:pb-4 md:pb-5 flex items-end justify-center px-2 sm:px-3 opacity-100 group-hover/face:opacity-0 transition-opacity duration-300">
          <span
            className="font-display text-white text-center font-extrabold uppercase leading-[1.05] tracking-wide"
            style={{
              fontSize: 'clamp(12px, 1.6vw, 20px)',
              textShadow:
                '0 2px 0 rgba(0,0,0,0.85), 0 0 12px rgba(0,0,0,0.95), 0 0 22px rgba(0,0,0,0.7), 0 0 28px rgba(0,240,255,0.35)',
            }}
          >
            {pick(s.title_es, s.title_en, s.title_pt)}
          </span>
        </div>

        {/* Bottom band that anchors the title without darkening the whole photo */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none opacity-100 group-hover/face:opacity-0 transition-opacity duration-300" />

        {/* HOVER OVERLAY — dark curtain + centered description with cyan accent lines */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center px-3 sm:px-4 md:px-5 text-center opacity-0 group-hover/face:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,10,20,0.55) 0%, rgba(0,0,0,0.72) 100%)',
          }}
        >
          <span className="w-8 h-px bg-gradient-to-r from-transparent via-cyan-300 to-transparent mb-2 opacity-90 transform origin-center scale-x-0 group-hover/face:scale-x-100 transition-transform duration-500 delay-100" />
          <span
            className="label-mono !text-cyan-300 text-[9px] sm:text-[10px] mb-1.5 tracking-[0.25em]"
            style={{ textShadow: '0 0 12px rgba(0,240,255,0.6)' }}
          >
            {pick(s.title_es, s.title_en, s.title_pt)}
          </span>
          <span
            className="font-display text-white font-semibold leading-[1.25] max-w-[92%] transform translate-y-2 group-hover/face:translate-y-0 transition-transform duration-400 delay-150"
            style={{
              fontSize: 'clamp(11px, 1.35vw, 17px)',
              textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 18px rgba(0,240,255,0.25)',
            }}
          >
            {pick(s.desc_es, s.desc_en, s.desc_pt)}
          </span>
          <span
            className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full border border-cyan-400/50 bg-cyan-400/10 backdrop-blur-sm font-tech uppercase tracking-wider text-[9px] sm:text-[10px] text-cyan-200 transform translate-y-2 opacity-0 group-hover/face:opacity-100 group-hover/face:translate-y-0 transition-all duration-400 delay-200"
          >
            {pick("Ver detalle", "See details", "Ver detalhes")} ›
          </span>
          <span className="w-8 h-px bg-gradient-to-r from-transparent via-cyan-300 to-transparent mt-2 opacity-90 transform origin-center scale-x-0 group-hover/face:scale-x-100 transition-transform duration-500 delay-100" />
        </div>
      </div>
    </Link>
  );
}

/**
 * Estilos globales para el marco cromado (bevel) + shimmer diagonal.
 * Se inyectan una sola vez cuando la primera tarjeta se monta.
 */
export const ServiceCardStyles = () => (
  <style>{`
    @keyframes metal-shimmer {
      0%   { transform: translateX(-130%) rotate(8deg); }
      60%  { transform: translateX(140%)  rotate(8deg); }
      100% { transform: translateX(140%)  rotate(8deg); }
    }
    .metal-card {
      padding: 5px;
      background:
        linear-gradient(135deg,
          #cfd6df 0%,
          #f4f7fa 12%,
          #8a93a1 28%,
          #2b313b 50%,
          #8a93a1 72%,
          #f4f7fa 88%,
          #cfd6df 100%);
      box-shadow:
        0 1px 0 rgba(255,255,255,0.45) inset,
        0 -1px 0 rgba(0,0,0,0.55) inset,
        0 22px 30px -16px rgba(0,0,0,0.85),
        0 0 0 1px rgba(0,0,0,0.6),
        0 0 28px -10px rgba(0,240,255,0.55);
      transition: box-shadow 0.5s ease, transform 0.4s ease;
    }
    .metal-card:hover {
      transform: translateY(-3px);
      box-shadow:
        0 1px 0 rgba(255,255,255,0.7) inset,
        0 -1px 0 rgba(0,0,0,0.55) inset,
        0 26px 38px -14px rgba(0,0,0,0.95),
        0 0 0 1px rgba(0,240,255,0.5),
        0 0 44px -6px rgba(0,240,255,0.75);
    }
    .metal-card .face {
      position: relative;
      overflow: hidden;
      border-radius: 16px;
      background:
        radial-gradient(60% 80% at 50% 0%, rgba(255,255,255,0.10) 0%, transparent 60%),
        radial-gradient(60% 60% at 50% 100%, rgba(0,0,0,0.55) 0%, transparent 70%),
        #0b1018;
      box-shadow:
        inset 0 1px 0 rgba(255,255,255,0.18),
        inset 0 -2px 0 rgba(0,0,0,0.6);
    }
    .metal-card .face::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%);
      mix-blend-mode: screen;
      animation: metal-shimmer 6.5s cubic-bezier(.4,0,.2,1) infinite;
      pointer-events: none;
    }
  `}</style>
);
